// Per-tab resume handle for an in-flight COMPARE PAIR (dual-engine comparison).
//
// Why a dedicated store (not PENDING_FORECAST_KEY): that single-slot key holds
// ONE pending forecast. A running compare pair has TWO independent SSE streams
// (one per engine/root), each with its own traceId/forecastId/clientId. If both
// legs wrote PENDING_FORECAST_KEY the second would clobber the first, so only one
// root would auto-reconnect on reload. We use an array shape instead: an entry per
// leg, all tagged with the same `pairId` so the resume sweep can recognize the two
// as a pair, restore the comparison selection, and resume BOTH streams.
//
// Single-forecast submits NEVER write here (the run-pair path is the only writer),
// so the single-forecast reload-resume path (PENDING_FORECAST_KEY) is untouched.
//
// Read-modify-write is atomic per call (JS is single-threaded; each helper runs as
// one microtask). Removed by pair once that invocation settles, or per-leg as each
// terminal event lands. Wholesale clear is reserved for global reset/sign-out.

export const COMPARE_PENDING_KEY = "pendingComparePair";

function _read() {
  try {
    return JSON.parse(sessionStorage.getItem(COMPARE_PENDING_KEY) || "[]");
  } catch {
    return [];
  }
}

function _write(arr) {
  try {
    sessionStorage.setItem(COMPARE_PENDING_KEY, JSON.stringify(arr));
  } catch {
    /* private mode / quota — best effort */
  }
}

// All leg handles for the current pair (array; [] when nothing pending).
export function readComparePending() {
  const arr = _read();
  return Array.isArray(arr) ? arr : [];
}

// Rewrite the store with only handles that are still recoverable. The resume sweep uses
// this after hydration, when it can distinguish a running root from a terminal/missing one.
// One write keeps pruning atomic and avoids quota growth from generations whose tab closed
// before their terminal cleanup ran.
export function pruneComparePending(keep) {
  const retained = readComparePending().filter(keep);
  _write(retained);
  return retained;
}

// Record (or replace) one leg's resume handle. Keyed by traceId so a re-fire on
// the same leg replaces rather than duplicates.
export function addComparePendingLeg(handle) {
  if (!handle || !handle.traceId) return;
  const arr = readComparePending().filter((h) => h.traceId !== handle.traceId);
  arr.push(handle);
  _write(arr);
}

// Drop one leg (its terminal event landed). When the last leg is removed the key
// is left as an empty array — readComparePending() then returns [], so the mount
// effect treats it as "nothing pending".
export function removeComparePendingLeg(traceId) {
  if (!traceId) return;
  const arr = readComparePending().filter((h) => h.traceId !== traceId);
  _write(arr);
}

// Drop every leg owned by one compare invocation without disturbing a newer pair
// that may already have written its handles into the same per-tab store.
export function removeComparePendingPair(pairId) {
  if (!pairId) return;
  const arr = readComparePending().filter((h) => h.pairId !== pairId);
  _write(arr);
}

// Wipe the whole pending-pair store (global reset or sign-out).
export function clearComparePending() {
  try {
    sessionStorage.removeItem(COMPARE_PENDING_KEY);
  } catch {
    /* ignore */
  }
}
